import { useState, useMemo, type JSX } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  AlertTriangle,
  Settings2,
  ArrowRight,
  AlertCircle,
  Info,
  Link2,
} from 'lucide-react';

// Hierarchy node types
export type HierarchyNodeType = 'epic' | 'story' | 'subtask' | 'idea' | 'theme';

export interface HierarchyNode {
  id: string;
  title: string;
  description?: string;
  type: HierarchyNodeType;
  sourceType: 'miro' | 'jira' | 'internal';
  sourceId?: string;
  linkedId?: string; // External system ID if linked
  isNew?: boolean;
  isModified?: boolean;
  isLinked?: boolean;
  hasConflict?: boolean;
  conflictMessage?: string;
  children?: HierarchyNode[];
  metadata?: {
    votes?: number;
    color?: string;
    author?: string;
    status?: string;
    priority?: string;
  };
}

export interface FieldMapping {
  sourceField: string;
  targetField: string;
  transform?: 'direct' | 'append' | 'prefix' | 'custom';
  customTransform?: string;
  isRequired?: boolean;
  hasWarning?: boolean;
  warningMessage?: string;
}

interface HierarchyMappingPreviewProps {
  nodes: HierarchyNode[];
  direction: 'export' | 'import';
  targetSystem: 'miro' | 'jira';
  fieldMappings?: FieldMapping[];
  onFieldMappingChange?: (mappings: FieldMapping[]) => void;
  onNodeEdit?: (nodeId: string, updates: Partial<HierarchyNode>) => void;
  onNodeTypeChange?: (nodeId: string, newType: HierarchyNodeType) => void;
  hasHierarchyConflict?: boolean;
  hierarchyConflictMessage?: string;
  onResolveConflict?: () => void;
}

const TYPE_COLORS: Record<HierarchyNodeType, { bg: string; text: string; border: string }> = {
  epic: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
  story: { bg: 'bg-[var(--slate-dim)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--slate)]' },
  subtask: { bg: 'bg-[var(--hover-tint)]', text: 'text-[var(--text-sec)]', border: 'border-[var(--rule)]' },
  idea: { bg: 'bg-[var(--mustard-dim)]', text: 'text-[var(--mustard)]', border: 'border-[var(--mustard)]' },
  theme: { bg: 'bg-[var(--green-dim)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', border: 'border-[var(--green)]' },
};

const TYPE_ICONS: Record<HierarchyNodeType, string> = {
  epic: '📦',
  story: '📋',
  subtask: '✔️',
  idea: '💡',
  theme: '🎯',
};

const DEFAULT_JIRA_MAPPINGS: FieldMapping[] = [
  { sourceField: 'title', targetField: 'summary', transform: 'direct', isRequired: true },
  { sourceField: 'description', targetField: 'description', transform: 'direct' },
  { sourceField: 'category', targetField: 'labels', transform: 'append' },
  { sourceField: 'score', targetField: 'customfield_priority_score', transform: 'direct', hasWarning: true, warningMessage: 'Requires custom field permission' },
  { sourceField: 'rank', targetField: 'rank', transform: 'direct' },
];

const DEFAULT_MIRO_MAPPINGS: FieldMapping[] = [
  { sourceField: 'title', targetField: 'content', transform: 'direct', isRequired: true },
  { sourceField: 'description', targetField: 'content', transform: 'append' },
  { sourceField: 'category', targetField: 'style.fillColor', transform: 'custom', customTransform: 'categoryToColor' },
  { sourceField: 'rank', targetField: 'position', transform: 'custom', customTransform: 'rankToPosition' },
];

export function HierarchyMappingPreview({
  nodes,
  direction,
  targetSystem,
  fieldMappings,
  onFieldMappingChange,
  onNodeEdit: _onNodeEdit,
  onNodeTypeChange,
  hasHierarchyConflict,
  hierarchyConflictMessage,
  onResolveConflict,
}: HierarchyMappingPreviewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(nodes.map(n => n.id)));
  const [showFieldMappings, setShowFieldMappings] = useState(false);
  const [mappings, setMappings] = useState<FieldMapping[]>(
    fieldMappings || (targetSystem === 'jira' ? DEFAULT_JIRA_MAPPINGS : DEFAULT_MIRO_MAPPINGS)
  );

  // Calculate stats
  const stats = useMemo(() => {
    const countNodes = (nodeList: HierarchyNode[]): { total: number; byType: Record<HierarchyNodeType, number>; conflicts: number; linked: number } => {
      let total = 0;
      let conflicts = 0;
      let linked = 0;
      const byType: Record<HierarchyNodeType, number> = { epic: 0, story: 0, subtask: 0, idea: 0, theme: 0 };
      
      const traverse = (n: HierarchyNode) => {
        total++;
        byType[n.type]++;
        if (n.hasConflict) conflicts++;
        if (n.isLinked) linked++;
        n.children?.forEach(traverse);
      };
      
      nodeList.forEach(traverse);
      return { total, byType, conflicts, linked };
    };
    
    return countNodes(nodes);
  }, [nodes]);

  const toggleExpand = (nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleMappingChange = (index: number, field: keyof FieldMapping, value: string) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setMappings(newMappings);
    onFieldMappingChange?.(newMappings);
  };

  const renderNode = (node: HierarchyNode, depth = 0): JSX.Element => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const colors = TYPE_COLORS[node.type];
    
    return (
      <div key={node.id} className="relative">
        {/* Connection line */}
        {depth > 0 && (
          <div 
            className="absolute left-0 top-0 w-4 h-4 border-l-2 border-b-2 border-[var(--rule)]" 
            style={{ marginLeft: `${(depth - 1) * 24 + 12}px` }}
          />
        )}
        
        <div 
          className={`flex items-start gap-2 p-2  border ${colors.border} ${colors.bg} transition-all hover:shadow-sm`}
          style={{ marginLeft: `${depth * 24}px` }}
        >
          {/* Expand/Collapse */}
          {hasChildren ? (
            <button 
              onClick={() => toggleExpand(node.id)}
              className="p-0.5 hover:bg-[var(--bg-raised)]/50 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-[var(--text-sec)]" />
              ) : (
                <ChevronRight className="h-4 w-4 text-[var(--text-sec)]" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}
          
          {/* Type Badge */}
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors.bg} ${colors.text} border ${colors.border}`}>
            {TYPE_ICONS[node.type]} {node.type.toUpperCase()}
          </span>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-[var(--text-pri)] truncate">{node.title}</span>
              {node.isNew && (
                <span className="text-xs bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-1.5 py-0.5 rounded font-medium">NEW</span>
              )}
              {node.isModified && (
                <span className="text-xs bg-[var(--mustard-dim)] text-[var(--mustard)] px-1.5 py-0.5 rounded font-medium">MODIFIED</span>
              )}
              {node.isLinked && (
                <span className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
                  <Link2 className="h-3 w-3" />
                  {node.linkedId}
                </span>
              )}
            </div>
            {node.description && (
              <p className="text-xs text-[var(--text-sec)] mt-0.5 line-clamp-1">{node.description}</p>
            )}
            {node.metadata && (
              <div className="flex items-center gap-2 mt-1 text-xs text-[var(--text-ter)]">
                {node.metadata.votes !== undefined && <span>👍 {node.metadata.votes}</span>}
                {node.metadata.author && <span>by {node.metadata.author}</span>}
                {node.metadata.status && <span className="px-1.5 py-0.5 bg-[var(--hover-tint)] rounded">{node.metadata.status}</span>}
              </div>
            )}
          </div>
          
          {/* Conflict Warning */}
          {node.hasConflict && (
            <div className="flex items-center gap-1 text-[var(--mustard)]" title={node.conflictMessage}>
              <AlertTriangle className="h-4 w-4" />
            </div>
          )}
          
          {/* Type Selector (for editing) */}
          {onNodeTypeChange && (
            <select
              value={node.type}
              onChange={(e) => onNodeTypeChange(node.id, e.target.value as HierarchyNodeType)}
              className="text-xs border border-[var(--rule)] rounded px-1.5 py-0.5 bg-[var(--bg-raised)]"
            >
              <option value="epic">Epic</option>
              <option value="story">Story</option>
              <option value="subtask">Sub-task</option>
            </select>
          )}
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {node.children!.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Hierarchy Conflict Warning */}
      {hasHierarchyConflict && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-[var(--mustard)]">Hierarchy Conflict Detected</h4>
              <p className="text-sm text-[var(--mustard)] mt-1">
                {hierarchyConflictMessage || 'Some items do not fit into the expected hierarchy structure.'}
              </p>
              {onResolveConflict && (
                <button
                  onClick={onResolveConflict}
                  className="mt-2 text-sm font-medium text-[var(--mustard)] hover:text-[var(--mustard)] underline"
                >
                  Resolve Conflicts →
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Summary */}
      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1.5 px-2 py-1 bg-[var(--hover-tint)] rounded">
          <Layers className="h-4 w-4 text-[var(--text-sec)]" />
          {stats.total} items
        </span>
        {stats.byType.epic > 0 && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
            {TYPE_ICONS.epic} {stats.byType.epic} Epics
          </span>
        )}
        {stats.byType.story > 0 && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
            {TYPE_ICONS.story} {stats.byType.story} Stories
          </span>
        )}
        {stats.byType.subtask > 0 && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
            {TYPE_ICONS.subtask} {stats.byType.subtask} Sub-tasks
          </span>
        )}
        {stats.conflicts > 0 && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded">
            <AlertTriangle className="h-3 w-3" /> {stats.conflicts} conflicts
          </span>
        )}
        {stats.linked > 0 && (
          <span className="flex items-center gap-1 px-2 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded">
            <Link2 className="h-3 w-3" /> {stats.linked} linked
          </span>
        )}
      </div>
      
      {/* Field Mappings Toggle */}
      <div className="border border-[var(--rule)] overflow-hidden">
        <button
          onClick={() => setShowFieldMappings(!showFieldMappings)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-[var(--text-sec)]">
            <Settings2 className="h-4 w-4" />
            Field Mappings
          </span>
          <ChevronDown className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${showFieldMappings ? 'rotate-180' : ''}`} />
        </button>
        
        {showFieldMappings && (
          <div className="p-4 border-t border-[var(--rule)] bg-[var(--bg-raised)]">
            <div className="space-y-2">
              {mappings.map((mapping, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-2 w-40">
                    <span className="font-mono text-[var(--text-sec)]">{mapping.sourceField}</span>
                    {mapping.isRequired && <span className="text-[var(--coral)]">*</span>}
                  </div>
                  <ArrowRight className="h-4 w-4 text-[var(--text-ter)]" />
                  <input
                    type="text"
                    value={mapping.targetField}
                    onChange={(e) => handleMappingChange(idx, 'targetField', e.target.value)}
                    className="flex-1 px-2 py-1 border border-[var(--rule)] rounded text-sm font-mono"
                  />
                  <select
                    value={mapping.transform || 'direct'}
                    onChange={(e) => handleMappingChange(idx, 'transform', e.target.value)}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] px-2 py-1 border border-[var(--rule)] rounded text-sm"
                  >
                    <option value="direct">Direct</option>
                    <option value="append">Append</option>
                    <option value="prefix">Prefix</option>
                    <option value="custom">Custom</option>
                  </select>
                  {mapping.hasWarning && (
                    <div className="flex items-center gap-1 text-[var(--mustard)]" title={mapping.warningMessage}>
                      <AlertCircle className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-3 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Unmapped fields will be added to the description as notes
            </p>
          </div>
        )}
      </div>
      
      {/* Hierarchy Preview */}
      <div className="border border-[var(--rule)] overflow-hidden">
        <div className="px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)]">
          <h4 className="text-sm font-medium text-[var(--text-sec)] flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Hierarchy Preview
            <span className="text-xs text-[var(--text-ter)]">
              ({direction === 'export' ? `→ ${targetSystem.toUpperCase()}` : `← ${targetSystem.toUpperCase()}`})
            </span>
          </h4>
        </div>
        
        <div className="p-4 space-y-2 max-h-96 overflow-y-auto bg-[var(--bg-raised)]">
          {nodes.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-ter)]">
              <Layers className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No items to preview</p>
            </div>
          ) : (
            nodes.map(node => renderNode(node))
          )}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-sec)] p-3 bg-[var(--surface)]">
        <span className="font-medium">Legend:</span>
        <span className="flex items-center gap-1">
          <span className="bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] px-1.5 py-0.5 rounded">NEW</span>
          Will be created
        </span>
        <span className="flex items-center gap-1">
          <span className="bg-[var(--mustard-dim)] text-[var(--mustard)] px-1.5 py-0.5 rounded">MODIFIED</span>
          Will be updated
        </span>
        <span className="flex items-center gap-1">
          <Link2 className="h-3 w-3 text-[var(--text-ter)]" />
          Already linked
        </span>
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-[var(--mustard)]" />
          Has conflict
        </span>
      </div>
    </div>
  );
}
